import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { retryBackoff, useLatestSubscription } from "app-base/web-shell/lifecycle"
import { AppData } from "./AppData"
import { createCoalescer, samePayload } from "./Coalesce"
import { Native } from "./Native"
import { Client } from "./Client"
import { Session } from "./Session"
import { useCompanionSession } from "./useCompanionSession"
import { Localization } from "./Localization"
import { localStorageId, localStorageValue } from "./LocalStorage"
import { logger, NATIVE } from "./Logger"
import { SubscribeRoute } from "./Routes"
import { IAppState, ILogbookAppStatusResult, ICurrentInfo, ILogbookSession, ILogbookSessionType, ISessionResponse, IUser } from "../Types"

// State cluster + effects extracted from the `App` component so that `App()` is
// just: call `useAppState()` → build the `app` `useMemo` → register effects with
// `useAppEffects(app, state)` → render. Strictly behaviour-preserving: the state,
// effect dependency arrays, subscription wiring, and closure-capture timing are
// unchanged from the original inline component.
export interface AppStateHook {
  lang: string;
  setLang: React.Dispatch<React.SetStateAction<string>>;
  accountSessionResponse: ISessionResponse | undefined;
  setAccountSessionResponse: React.Dispatch<React.SetStateAction<ISessionResponse | undefined>>;
  appStyles: string[];
  setAppStyles: React.Dispatch<React.SetStateAction<string[]>>;
  current: ICurrentInfo | undefined;
  setCurrent: React.Dispatch<React.SetStateAction<ICurrentInfo | undefined>>;
  features: string[] | undefined;
  setFeatures: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  location: string;
  setLocation: React.Dispatch<React.SetStateAction<string>>;
  n: number;
  setN: React.Dispatch<React.SetStateAction<number>>;
  session: ILogbookSession | undefined;
  setSession: React.Dispatch<React.SetStateAction<ILogbookSession | undefined>>;
  sessionType: ILogbookSessionType | undefined;
  setSessionType: React.Dispatch<React.SetStateAction<ILogbookSessionType | undefined>>;
  shipId: number | undefined;
  setShipId: React.Dispatch<React.SetStateAction<number | undefined>>;
  status: ILogbookAppStatusResult | undefined;
  setStatus: React.Dispatch<React.SetStateAction<ILogbookAppStatusResult | undefined>>;
  socket: Client;
  theme: string | undefined;
  setTheme: React.Dispatch<React.SetStateAction<string | undefined>>;
  user: IUser | undefined;
  setUser: React.Dispatch<React.SetStateAction<IUser | undefined>>;
}

// The language the app opens in, resolved once at mount. Precedence, highest
// first:
//
//   1. `logbook-language` in localStorage — whatever is stored wins,
//   2. the device language reported by the native shell (or `navigator` in a
//      browser / cloud session),
//   3. "en".
//
// A detected language is written to localStorage straight away, which is what
// makes it stick: @mutle decided the device language is locked in at first
// launch rather than re-read on every launch, so after the first run the stored
// value is the only source and a later device-language change does not move the
// app. Clearing localStorage deliberately re-syncs from the device.
//
// This runs in the `useState` initialiser rather than an effect on purpose —
// resolving after mount would repaint the whole UI, flashing English first.
// `Native.deviceLanguage` reads a global the shells inject before any page
// script runs, so it is available this early.
//
// Note that a *stored* value is used as-is, including "en": a user who picked
// English on a German device must not be pushed back to German.
function launchLanguage(): string {
  const stored = localStorageValue("logbook-language");
  if (stored) {
    document.documentElement.setAttribute("lang", stored);
    return stored;
  }
  const device = Native.deviceLanguage();
  if (device) {
    localStorage.setItem("logbook-language", device);
    document.documentElement.setAttribute("lang", device);
    return device;
  }
  // Nothing was decided — don't persist the fallback, so a device that is later
  // switched to a language we serve still gets to be detected.
  document.documentElement.setAttribute("lang", Localization.Fallback);
  return Localization.Fallback;
}

export function useAppState(): AppStateHook {
  const [lang, setLang] = useState<string>(launchLanguage());
  const [accountSessionResponse, setAccountSessionResponse] = useState<ISessionResponse | undefined>(undefined);
  const [appStyles, setAppStyles] = useState<string[]>(["default"]);
  const [current, setCurrent] = useState<ICurrentInfo | undefined>(undefined);
  const [features, setFeatures] = useState<string[] | undefined>(undefined);
  const [location, setLocation] = useState<string>(window.location.pathname);
  const [n, setN] = useState<number>(0);
  const [session, setSession] = useState<ILogbookSession | undefined>(undefined);
  const [sessionType, setSessionType] = useState<ILogbookSessionType | undefined>(undefined);
  const urlParams = new URLSearchParams(window.location.search);
  const forceAccountView = urlParams.get("account") === "true";
  const [shipId, setShipId] = useState<number | undefined>(forceAccountView ? undefined : localStorageId("logbook-ship"));
  const [status, setStatus] = useState<ILogbookAppStatusResult | undefined>(undefined);
  const [socket, _setSocket] = useState<Client>(new Client());
  const [theme, setTheme] = useState<string | undefined>(localStorageValue("logbook-theme"));
  const [user, setUser] = useState<IUser | undefined>(undefined);

  return {
    lang, setLang,
    accountSessionResponse, setAccountSessionResponse,
    appStyles, setAppStyles,
    current, setCurrent,
    features, setFeatures,
    location, setLocation,
    n, setN,
    session, setSession,
    sessionType, setSessionType,
    shipId, setShipId,
    status, setStatus,
    socket,
    theme, setTheme,
    user, setUser,
  };
}

// Registers the app's live-update wiring and lifecycle effects. Receives the
// already-built `app` so the render-body socket subscriptions and the effects use
// the same object the render provides. Called after the `app` `useMemo` in `App`;
// its `useCallback([])` subscription callbacks therefore capture the first-render
// `app` exactly as the original inline code did.
export function useAppEffects(app: IAppState, state: AppStateHook): void {
  useCompanionSession(app);
  const {
    lang, n, session, sessionType, shipId, status, socket,
    setSession, setSessionType, setShipId, setAccountSessionResponse, setUser, setFeatures,
    setTheme, setStatus, setN, setAppStyles, setLocation, setLang,
  } = state;

  const updateStatus = useCallback(async () => {
    if (!session || !session.authorized) {
      return;
    }
    if (app.cloudSession() || (status && status.server)) {
      return;
    }
    const newStatus = await app.updateStatus();
    if (newStatus) {
      newStatus.date = new Date().getTime();
      setStatus(newStatus);
      app.status = newStatus;
    }
  }, [session, status]);

  // `AppStatus` and `Day` broadcasts are the app's two highest-frequency socket
  // updates, and both used to dispatch a React update straight from `onmessage`.
  // A backlog burst — what the browser delivers when the display wakes or the tab
  // comes back to the foreground — therefore became a long run of commits that
  // each landed with the next message already queued, which is precisely what
  // drives React 19's nested-update counter to the #185 throw (see `Coalesce.ts`
  // for the mechanism and #1813 for the report). Buffering the burst and flushing
  // once from a macrotask collapses it into a single commit that lands with an
  // empty queue, resetting the counter rather than advancing it.
  const pendingStatus = useRef<ILogbookAppStatusResult | undefined>(undefined);
  const statusCoalescer = useMemo(() => createCoalescer(() => {
    const next = pendingStatus.current;
    pendingStatus.current = undefined;
    if (next == undefined) {
      return;
    }
    setStatus(next);
  }), [setStatus]);
  useEffect(() => () => statusCoalescer.cancel(), [statusCoalescer]);

  const statusSubscription = useCallback((status: ILogbookAppStatusResult, _type: string) => {
    // `app.status` is read directly (outside React) by callers that must see the
    // newest value immediately, so it is still assigned synchronously.
    const previous = app.status;
    app.status = status;
    // A moored boat re-broadcasts an unchanged status indefinitely; storing it
    // again would replace the `app` memo's identity and re-render the whole tree
    // for no visible change.
    if (samePayload(previous, status)) {
      return;
    }
    pendingStatus.current = status;
    statusCoalescer.schedule();
  }, [app, statusCoalescer]);
  useLatestSubscription(
    (listener) => {
      const id = app.socket.subscribe("AppStatus", listener);
      return () => app.socket.unsubscribe("AppStatus", id);
    },
    statusSubscription,
    [app.socket],
  );

  // Refresh `current` whenever a `Day` broadcast arrives. The handler is kept in a
  // ref (updated every render) so the single subscription below always invokes the
  // CURRENT-render app's `updateCurrent`, which closes over the live session/shipId.
  //
  // Bugfix (#769): this was previously a render-body `app.socket.subscribe("Day", …)`
  // with a `useCallback([])` handler that captured the render-0 app — whose
  // `updateCurrent` early-returns on the initial undefined session — so Day
  // broadcasts never refreshed `current`. It also re-subscribed (accumulating ids
  // per `Client.subscribe`) on every render. Subscribing once in an effect with a
  // latest-handler ref both fixes the stale capture and removes that leak.
  // Coalesced for the same reason as the status handler above: a backlog of `Day`
  // broadcasts used to fire one `updateCurrent()` refetch — and so one state
  // update — per message. The coalescer reads `app` through a ref so it stays
  // stable across the `app` memo's identity churn; recreating it per render would
  // let the cleanup below cancel a scheduled refetch before it ran.
  const appRef = useRef(app);
  appRef.current = app;
  const currentCoalescer = useMemo(
    () => createCoalescer(() => { appRef.current.updateCurrent(); }),
    [],
  );
  useEffect(() => () => currentCoalescer.cancel(), [currentCoalescer]);
  useLatestSubscription(
    (listener) => {
      const id = app.socket.subscribe("Day", listener);
      return () => app.socket.unsubscribe("Day", id);
    },
    () => { currentCoalescer.schedule(); },
    [app.socket],
  );

  useEffect(() => {
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  useEffect(() => {
    if (session != undefined) {
      return;
    }
    if (!app.cloudSession() && Session.isCompanionRoute(window.location.pathname)) {
      // Render the handoff without authenticating (or logging out) the origin's old token.
      setSession({ authorized: false });
      return;
    }
    // The guard above is evaluated when this effect RUNS; `superseded` is what
    // re-checks it when the request RESOLVES. Between the two, a session can be
    // established by another path -- most commonly the user logging in through
    // the form while the boot-time load is still outstanding, which is routine
    // because the app answers `/api/session` with 401 until its local DB is
    // ready and a ship select restarts the instance.
    //
    // Without this, that stale rejection reached the catch below and called
    // `app.logout(false)`, wiping the session that had just succeeded and
    // bouncing the user back to the login page. Seen in a full E2E run of
    // `nmea-live-0183.spec.ts`: the server logged `ship selected 8` while the
    // browser sat on `/app/signup`, having logged itself out.
    //
    // The success path is guarded too: applying a stale session/account/user on
    // top of a newer one is the same bug in a quieter form.
    let superseded = false;
    logger.debug("loading session", NATIVE);
    const load = async () => {
      // If localStorage lost the session (e.g. wiped by an app update) but the
      // device still holds its primary session server-side, restore it on the
      // launch route so the user isn't forced to log in again. Transient failures
      // must reach the retry handler, not an unauthenticated load that logs out.
      if (Session.shouldRestore(window.location.pathname, app.cloudSession())) {
        const restored = await AppData.restoreSession(app.apiClient);
        if (superseded) {
          logger.debug("session restore superseded, discarding", NATIVE);
          return;
        }
        if (restored && restored.session && restored.session.authorized && restored.session.id) {
          logger.debug("restored session", NATIVE);
          Session.persistSession(socket, restored.session, restored.type);
        }
      }
      const data = await AppData.loadSession(app.apiClient);
      if (!data || !data.session) {
        // The generated type declares `session` as non-optional, so this is a
        // defensive guard -- but it used to be an `if` with no `else`, which
        // left `session` undefined AND `n` untouched. The effect's [session, n]
        // deps therefore never changed, it never re-ran, and the app rested on
        // <Loading /> forever with no retry, no logout and nothing logged; even
        // a reload didn't help (#1300). Throw so the empty case takes the same
        // escalating-backoff retry as a transient failure.
        throw new Error("session load resolved without a session");
      }
      if (superseded) {
        logger.debug("session load superseded, discarding", NATIVE);
        return;
      }
      setSessionType(data.type);
      setSession(data.session);
      setAccountSessionResponse(data);
      setUser(data.user);
      setFeatures(data.features || []);

      // Apply the user's persisted language (cloud sessions store it
      // server-side). Primary/local sessions have no stored language, so the
      // localStorage value chosen at init is kept.
      if (data.user?.language) {
        setLang(data.user.language);
        localStorage.setItem("logbook-language", data.user.language);
      }

      // Force cloud theme when in cloud session
      if (data.type == ILogbookSessionType.cloud) {
        const cloudTheme = "cloud";
        setTheme(cloudTheme);
        localStorage.setItem("logbook-theme", cloudTheme);
        document.documentElement.setAttribute("data-theme", cloudTheme);
      }

      // #1043: a passive app reload/restart (as opposed to the manual
      // ship-select click, which already persists this via `setShipId`)
      // must resume a previously-open ship automatically. `session.get`
      // already returns the primary session's `shipId`; apply it here so
      // React state + localStorage reflect it without a second reload.
      // Only ever set — never clear — so this doesn't stomp a companion's
      // own `logbook-ship` (set independently via `connectLocal`, which
      // this device's own primary session never gets to see).
      if (data.type == ILogbookSessionType.primary && data.session.shipId != undefined) {
        Session.persistShipId(data.session.shipId);
        setShipId(data.session.shipId);
      }

      socket.stream(data.type == "primary" ? 1000 : 10000);
      app.updateCurrent();
      updateStatus();
      logger.debug("loaded session");
    };
    load().catch((error) => {
      // A genuinely invalid/expired session responds HTTP 200 with
      // `authorized: false` (handled in the success branch above), so reaching
      // this catch means the load itself failed -- or resolved without a
      // session, which the branch above turns into a throw so it lands here too.
      // Only a real auth rejection (401/403) ends the session; everything else
      // -- a total network failure (no HTTP response, e.g. the server
      // unreachable mid-deploy), a 5xx from the app/proxy, and the 503 the app
      // returns while its local DB isn't ready yet -- keeps the user signed in
      // and retries.
      if (superseded) {
        logger.debug("session load failed but was superseded, ignoring", error, NATIVE);
        return;
      }
      if (Session.classifySessionLoadFailure(error) == "logout") {
        logger.debug("session load failed (definitive), logging out", error, NATIVE);
        app.logout(false);
        return;
      }
      // Retry with an escalating backoff — quick first (250ms) then doubling up
      // to a 10s cap — and never clear the stored token, just like the
      // WebSocket layer reconnects instead of giving up. `session` stays
      // undefined so the app keeps showing its <Loading /> state until a later
      // attempt succeeds.
      const delay = retryBackoff({
        initialMilliseconds: 250,
        maximumMilliseconds: 10000,
      })(n);
      logger.debug("session load failed, retrying", error, NATIVE);
      window.setTimeout(() => {
        if (superseded) {
          return;
        }
        logger.debug("retrying session load", NATIVE);
        setN(n + 1);
      }, delay);
    });
    return () => { superseded = true; };
  }, [session, n]);

  useEffect(() => {
    const load = async () => {
      if (shipId && sessionType) {
        updateStatus();
        app.updateCurrent();
      }
    };
    load().catch(console.error);
  }, [shipId, sessionType]);

  useEffect(() => {
    const load = async () => {
      const styles = await Native.loadAppStyles();
      if (styles) {
        app.appStyles = styles;
        setAppStyles(styles);
      }
    };
    load().catch(console.error);

    if (document.location.search.indexOf("inapp=true") >= 0) {
      localStorage.setItem("logbook-inapp", "true");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  useLatestSubscription(
    SubscribeRoute,
    setLocation,
    [],
  );
}
